import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { formatApiErrorDetail } from '../utils/api';
import { Heart, Mail, Lock, ArrowRight } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.from || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: '#FDFBF7' }}>
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ backgroundColor: '#F3EFE7' }}>
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1497621122273-f5cfb6065c56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwxfHxvcGVuJTIwYmlibGUlMjBzdW5saWdodHxlbnwwfHx8fDE3ODQ4NTgyNDB8MA&ixlib=rb-4.1.0&q=85"
            alt="Bible"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(44, 39, 36, 0.55) 0%, rgba(44, 39, 36, 0.65) 60%, rgba(92, 113, 94, 0.55) 100%)' }}></div>
        </div>
        <div className="relative z-10 p-12 flex flex-col justify-center">
          <blockquote className="text-3xl font-medium italic border-l-4 pl-6 mb-6" style={{ fontFamily: 'Lora, serif', color: '#FDFBF7', borderColor: '#E57A44', textShadow: '0 2px 12px rgba(0,0,0,0.5)' }}>
            "Love is patient, love is kind. It does not envy, it does not boast, it is not proud."
          </blockquote>
          <p className="text-lg" style={{ fontFamily: 'Manrope, sans-serif', color: '#FDFBF7', fontWeight: 600, letterSpacing: '0.05em', textShadow: '0 2px 10px rgba(0,0,0,0.6)' }}>1 Corinthians 13:4</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <Link to="/" className="flex items-center gap-3 mb-8 group">
            <div className="p-2 rounded-xl transition-all duration-300" style={{ backgroundColor: '#E57A44' }}>
              <Heart className="w-6 h-6" style={{ color: '#FDFBF7', strokeWidth: 2 }} />
            </div>
            <span className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>Love Restored</span>
          </Link>

          <h2 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>Welcome Back</h2>
          <p className="mb-8" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D' }}>Continue your journey of relationship restoration</p>

          {error && (
            <div className="mb-6 p-4 rounded-xl" style={{ backgroundColor: '#FEE2E2', border: '1px solid #FCA5A5' }} data-testid="login-error">
              <p style={{ fontFamily: 'Manrope, sans-serif', color: '#991B1B', fontSize: '14px' }}>{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', fontWeight: 500, fontSize: '14px' }}>Email</label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#8C867D', strokeWidth: 2 }} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full pl-12 pr-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2"
                  style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6D5B8', color: '#2C2724' }}
                  placeholder="your@email.com"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724', fontWeight: 500, fontSize: '14px' }}>Password</label>
                <Link
                  to="/forgot-password"
                  className="text-xs transition-colors"
                  style={{ fontFamily: 'Manrope, sans-serif', color: '#E57A44', fontWeight: 600 }}
                  data-testid="login-forgot-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: '#8C867D', strokeWidth: 2 }} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="w-full pl-12 pr-4 py-3 rounded-xl border transition-all duration-300 focus:outline-none focus:ring-2"
                  style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6D5B8', color: '#2C2724' }}
                  placeholder="••••••••"
                  data-testid="login-password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 rounded-xl transition-all duration-300 hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 600 }}
              data-testid="login-submit"
            >
              <span>{loading ? 'Logging in...' : 'Login'}</span>
              {!loading && <ArrowRight className="w-5 h-5" style={{ strokeWidth: 2 }} />}
            </button>
          </form>

          <p className="mt-8 text-center" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D' }}>
            Don't have an account?{' '}
            <Link to="/register" state={location.state} className="font-semibold transition-colors duration-300" style={{ color: '#E57A44' }} data-testid="login-register-link">
              Register
            </Link>
          </p>

          <p className="mt-12 text-center" data-testid="footer-attribution" style={{ fontFamily: 'Manrope, sans-serif', color: '#8C867D', fontSize: '13px' }}>
            Created with love by <span style={{ color: '#E57A44', fontWeight: 600 }}>Genesis Natera</span>
          </p>
        </div>
      </div>
    </div>
  );
}
